import { useMap } from "@vis.gl/react-maplibre";
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { getAliveMap } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  createAreaWaypoint,
  IAreaWaypoint,
  IPlacePrediction,
  isAreaWaypointAlreadySelected,
  IStationRouteWaypointLocationDraft,
  MAX_AREA_WAYPOINTS_PER_DIRECTION,
  resolveStationRouteWaypointName,
  StationRouteWaypointAddMode,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export interface IUseEditAreaWaypointsMapControlsParams {
  waypoints: IAreaWaypoint[];
  disabled?: boolean;
  onAddWaypoint: (waypoint: IAreaWaypoint) => void;
}

export const useEditAreaWaypointsMapControls = ({
  waypoints,
  disabled,
  onAddWaypoint,
}: IUseEditAreaWaypointsMapControlsParams) => {
  const { current: mapRef } = useMap();
  const mapContainer = getAliveMap(mapRef)?.getContainer() ?? null;
  const { t } = useTranslation();
  const { getCoordinateByPlaceId } = useGoogleServices();
  const anchorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const popperContentRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [addMode, setAddMode] = useState<StationRouteWaypointAddMode>(
    StationRouteWaypointAddMode.None,
  );
  const [addressDraft, setAddressDraft] = useState("");
  const [customNameDraft, setCustomNameDraft] = useState("");
  const [pendingDraft, setPendingDraft] = useState<
    IStationRouteWaypointLocationDraft | undefined
  >(undefined);
  const [addressOptions, setAddressOptions] = useState<IPlacePrediction[]>([]);

  const isPopperOpen =
    isMenuOpen ||
    addMode === StationRouteWaypointAddMode.Search ||
    Boolean(pendingDraft);

  const panMapTo = useEvent((latitude: number, longitude: number) => {
    getAliveMap(mapRef)?.easeTo({
      center: [longitude, latitude],
      duration: 300,
    });
  });

  const closeAll = useEvent(() => {
    setIsMenuOpen(false);
    setAddMode(StationRouteWaypointAddMode.None);
    setPendingDraft(undefined);
    setAddressDraft("");
    setCustomNameDraft("");
    setAddressOptions([]);
  });

  const handleClickAway = useEvent((event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }

    closeAll();
  });

  const canAddWaypoint =
    !disabled && waypoints.length < MAX_AREA_WAYPOINTS_PER_DIRECTION;

  const handleTriggerClick = useEvent(() => {
    if (!canAddWaypoint) {
      return;
    }

    if (addMode === StationRouteWaypointAddMode.MapClick) {
      return;
    }

    if (
      isMenuOpen ||
      addMode === StationRouteWaypointAddMode.Search ||
      pendingDraft
    ) {
      closeAll();
      return;
    }

    setIsMenuOpen(true);
  });

  const startSearchMode = useEvent(() => {
    setIsMenuOpen(true);
    setAddMode(StationRouteWaypointAddMode.Search);
  });

  const startMapClickMode = useEvent(() => {
    setIsMenuOpen(false);
    setAddMode(StationRouteWaypointAddMode.MapClick);
    setPendingDraft(undefined);
    setAddressDraft("");
  });

  const handleLocationPicked = useEvent(
    (draft: IStationRouteWaypointLocationDraft) => {
      setPendingDraft(draft);
      setAddressDraft(draft.fullAddress);
      setAddMode(StationRouteWaypointAddMode.Search);
      setIsMenuOpen(true);
      panMapTo(draft.latitude, draft.longitude);
    },
  );

  const handleAddressChange = useEvent(
    async (newValue: IPlacePrediction | null) => {
      if (!newValue) {
        setAddressDraft("");
        setPendingDraft(undefined);
        return;
      }

      const locationResult = await getCoordinateByPlaceId(newValue.placeId);
      const { success, data } = locationResult ?? {};

      if (!success || !data?.lat || !data?.lng) {
        setPendingDraft(undefined);
        setAddressDraft("");
        warnAndToastSimpleResult(locationResult, t);
        return;
      }

      const draft: IStationRouteWaypointLocationDraft = {
        fullAddress: newValue.text?.text ?? "",
        latitude: data.lat,
        longitude: data.lng,
      };
      setAddressDraft(draft.fullAddress);
      setPendingDraft(draft);
      panMapTo(draft.latitude, draft.longitude);
    },
  );

  const handleCustomNameChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCustomNameDraft(event.target.value);
    },
  );

  const handleConfirm = useEvent(() => {
    if (!pendingDraft?.latitude || !pendingDraft?.longitude) {
      return;
    }

    const fullAddress = addressDraft || pendingDraft.fullAddress;
    const name = resolveStationRouteWaypointName({
      customName: customNameDraft,
      fullAddress,
      fallbackName: t("stationRoute.waypoint.DEFAULT_NAME"),
    });
    const waypoint = createAreaWaypoint({
      name,
      latitude: pendingDraft.latitude,
      longitude: pendingDraft.longitude,
      fullAddress,
    });

    if (isAreaWaypointAlreadySelected(waypoints, waypoint)) {
      return;
    }

    onAddWaypoint(waypoint);
    closeAll();
  });

  const addressValue = useMemo(
    () =>
      addressDraft
        ? ({
            placeId: "",
            text: { text: addressDraft },
          } as IPlacePrediction)
        : null,
    [addressDraft],
  );

  const canConfirm = Boolean(
    pendingDraft?.latitude && pendingDraft?.longitude && canAddWaypoint,
  );
  const isButtonActive =
    isMenuOpen || addMode !== StationRouteWaypointAddMode.None;
  const isSearchPanelOpen =
    addMode === StationRouteWaypointAddMode.Search || Boolean(pendingDraft);
  const isMapClickMode = addMode === StationRouteWaypointAddMode.MapClick;

  return {
    mapContainer,
    anchorRef,
    toolbarRef,
    popperContentRef,
    t,
    addressOptions,
    addressValue,
    canAddWaypoint,
    canConfirm,
    closeAll,
    customNameDraft,
    handleAddressChange,
    handleClickAway,
    handleConfirm,
    handleCustomNameChange,
    handleLocationPicked,
    handleTriggerClick,
    isButtonActive,
    isMapClickMode,
    isPopperOpen,
    isSearchPanelOpen,
    pendingDraft,
    setAddressOptions,
    startMapClickMode,
    startSearchMode,
  };
};
